import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatAxiosError } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = guest, obj = signed in
  const [booted, setBooted] = useState(false);

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // 401 here is the normal "anonymous visitor" case — log at debug
      // so genuine network failures are still visible in devtools
      // without polluting the console for every logged-out page load.
      if (process.env.NODE_ENV !== "production") {
        console.debug("auth bootstrap (no session):", e?.response?.status || e?.message);
      }
      setUser(false);
    } finally {
      setBooted(true);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("decal_token", data.access_token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatAxiosError(e) };
    }
  };

  const register = async (email, password, name, country) => {
    try {
      const { data } = await api.post("/auth/register", {
        email,
        password,
        name,
        country: country || "ZA",
      });
      if (data.access_token) localStorage.setItem("decal_token", data.access_token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role, country: data.country });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatAxiosError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // The server-side cookie clear is best-effort — if it fails we
      // still proceed with the local logout below so the user isn't
      // stuck in a half-logged-in state. Log so we'd see a pattern if
      // it started failing consistently in production.
      console.warn("server logout failed (continuing locally):", e?.message);
    }
    localStorage.removeItem("decal_token");
    setUser(false);
  };

  const refresh = useCallback(async () => {
    // Re-pull /auth/me so impersonation changes update the UI immediately.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Refresh failure (after the user is already logged in) is rare
      // and usually transient — leave the cached `user` in place rather
      // than booting them to /login on a flaky network blip.
      console.warn("auth refresh failed:", e?.response?.status || e?.message);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, booted, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
